import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  BarChart3, Loader2, RefreshCw, TrendingUp, Bot, Users, Globe, MessageCircle, Star,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const Bar = ({ value, max, color = "bg-[#002FA7]" }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-1.5 bg-gray-200 overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const RANGES = [
  { d: 7, label: "7d" },
  { d: 30, label: "30d" },
  { d: 90, label: "90d" },
];

const UA_COLORS = {
  human: "bg-green-500",
  llm_bot: "bg-purple-500",
  seo_bot: "bg-blue-500",
  social_bot: "bg-pink-500",
  other_bot: "bg-gray-400",
  unknown: "bg-gray-300",
};
const UA_LABELS = {
  human: "Mensen",
  llm_bot: "LLM-crawlers",
  seo_bot: "Zoekmachines",
  social_bot: "Social previews",
  other_bot: "Andere bots",
  unknown: "Onbekend",
};

const REFERRER_LABELS = {
  direct: "Direct",
  search: "Zoekmachine",
  social: "Social media",
  llm_referral: "AI-chat referral",
  internal: "Intern",
  other: "Anders",
};

const SparkLine = ({ points }) => {
  if (!points || points.length === 0) return <p className="text-[10px] text-gray-400 italic">geen data</p>;
  const max = Math.max(...points.map(p => p.count), 1);
  const w = 240, h = 40, step = points.length > 1 ? w / (points.length - 1) : 0;
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${i * step},${h - (p.count / max) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="block">
      <path d={path} stroke="#002FA7" strokeWidth="2" fill="none" />
      {points.map((p, i) => (
        <circle key={`sp-${p.day}-${p.count}`} cx={i * step} cy={h - (p.count / max) * h} r="2" fill="#002FA7" />
      ))}
    </svg>
  );
};

export const StatsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("overview"); // overview | traffic | topics | support

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/stats/dashboard?days=${days}`);
      setData(r.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[Stats] failed:", err?.message);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Ensure indexes exist (idempotent)
  useEffect(() => {
    axios.post(`${API}/stats/ensure-indexes`).catch(() => {});
  }, []);

  const traffic = data?.traffic || {};
  const debates = data?.debates || {};
  const support = data?.support || {};

  const startedMax = Math.max(...((debates.started_by_day || []).map(d => d.count)), 1);
  const totalStarted = (debates.started_by_day || []).reduce((s, d) => s + d.count, 0);
  const totalCompleted = (debates.completed_by_day || []).reduce((s, d) => s + d.count, 0);
  const completionRate = totalStarted > 0 ? Math.round((totalCompleted / totalStarted) * 100) : 0;

  const uaTotal = (traffic.by_ua_class || []).reduce((s, d) => s + d.count, 0);

  return (
    <div className="bg-white border-2 border-gray-200 shadow-sm" data-testid="stats-panel">
      <div className="bg-gray-900 text-white px-4 py-3 flex items-center justify-between">
        <span className="flex items-center gap-2 font-bold text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>
          <BarChart3 className="w-4 h-4" />
          Stats
        </span>
        <div className="flex items-center gap-2">
          {RANGES.map(r => (
            <button key={r.label}
              onClick={() => setDays(r.d)}
              className={`text-[10px] px-1.5 py-0.5 ${days === r.d ? "bg-white text-gray-900" : "text-gray-300 hover:text-white"}`}
              data-testid={`stats-range-${r.label}`}
            >{r.label}</button>
          ))}
          <button onClick={load} disabled={loading} className="opacity-80 hover:opacity-100" title="Vernieuwen" data-testid="stats-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 text-[10px] font-bold uppercase tracking-wider">
        {[
          { id: "overview", label: "Overzicht", Icon: TrendingUp },
          { id: "traffic", label: "Verkeer", Icon: Globe },
          { id: "topics", label: "Topics", Icon: MessageCircle },
          { id: "support", label: "Support", Icon: Users },
        ].map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 ${tab === t.id ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}
            data-testid={`stats-tab-${t.id}`}
          >
            <t.Icon className="w-3 h-3" />
            {t.label}
          </button>
        ))}
      </div>

      {!data && loading && (
        <div className="p-6 flex justify-center text-gray-500 text-sm gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Stats laden...
        </div>
      )}

      {data && tab === "overview" && (
        <div className="p-3 space-y-3" data-testid="stats-overview">
          <div className="grid grid-cols-2 gap-2">
            <div className="border border-gray-200 p-2">
              <p className="text-[9px] font-mono text-gray-500 uppercase">Debatten gestart</p>
              <p className="text-2xl font-black">{totalStarted}</p>
            </div>
            <div className="border border-gray-200 p-2">
              <p className="text-[9px] font-mono text-gray-500 uppercase">Voltooid</p>
              <p className="text-2xl font-black">{totalCompleted}</p>
              <p className="text-[10px] text-gray-500">{completionRate}%</p>
            </div>
            <div className="border border-gray-200 p-2">
              <p className="text-[9px] font-mono text-gray-500 uppercase">Unieke bezoekers</p>
              <p className="text-2xl font-black">{traffic.unique_human_visitors || 0}</p>
            </div>
            <div className="border border-gray-200 p-2">
              <p className="text-[9px] font-mono text-gray-500 uppercase">Page-views</p>
              <p className="text-2xl font-black">{traffic.total_pageviews || 0}</p>
            </div>
          </div>
          <div className="border border-gray-200 p-2">
            <p className="text-[10px] font-mono uppercase text-gray-500 mb-1">Debatten gestart per dag</p>
            <SparkLine points={debates.started_by_day} />
          </div>
          <div className="border border-gray-200 p-2 flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5"><Star className="w-3 h-3 text-amber-500" /> Reviews ontvangen</span>
            <span className="font-bold">{data.feedback?.total || 0}</span>
          </div>
        </div>
      )}

      {data && tab === "traffic" && (
        <div className="p-3 space-y-3" data-testid="stats-traffic">
          <div>
            <p className="text-[10px] font-mono uppercase text-gray-500 mb-2 flex items-center gap-1"><Bot className="w-3 h-3" /> Bezoeker-type</p>
            {(traffic.by_ua_class || []).length === 0 ? (
              <p className="text-[11px] text-gray-500 italic">Nog geen page-view data. Wacht op verkeer of test in een prive-venster.</p>
            ) : (traffic.by_ua_class || []).map(row => {
              const pct = uaTotal > 0 ? Math.round((row.count / uaTotal) * 100) : 0;
              return (
                <div key={row.class} className="mb-1.5" data-testid={`stats-ua-${row.class}`}>
                  <div className="flex justify-between text-[11px] mb-0.5">
                    <span>{UA_LABELS[row.class] || row.class}</span>
                    <span className="font-mono">{row.count} ({pct}%)</span>
                  </div>
                  <Bar value={row.count} max={uaTotal} color={UA_COLORS[row.class] || "bg-gray-400"} />
                </div>
              );
            })}
          </div>

          {(traffic.by_ua_label_bots || []).length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase text-gray-500 mb-2">Bots in detail</p>
              <div className="space-y-1 max-h-[180px] overflow-y-auto">
                {traffic.by_ua_label_bots.map(b => (
                  <div key={b.label} className="flex justify-between text-[11px] border-b border-gray-100 py-0.5">
                    <span>{b.label}</span>
                    <span className="font-mono font-bold">{b.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(traffic.by_referrer_humans || []).length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase text-gray-500 mb-2">Verwijzers (alleen mensen)</p>
              {traffic.by_referrer_humans.map(r => (
                <div key={r.bucket} className="flex justify-between text-[11px] py-0.5">
                  <span>{REFERRER_LABELS[r.bucket] || r.bucket}</span>
                  <span className="font-mono font-bold">{r.count}</span>
                </div>
              ))}
            </div>
          )}

          {(traffic.by_path_humans || []).length > 0 && (
            <div>
              <p className="text-[10px] font-mono uppercase text-gray-500 mb-2">Populairste pagina's</p>
              <div className="space-y-0.5 max-h-[180px] overflow-y-auto">
                {traffic.by_path_humans.map(p => (
                  <div key={p.path} className="flex justify-between text-[11px] py-0.5">
                    <span className="font-mono truncate mr-2">{p.path}</span>
                    <span className="font-bold flex-shrink-0">{p.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {data && tab === "topics" && (
        <div className="p-3 space-y-2" data-testid="stats-topics">
          <p className="text-[10px] font-mono uppercase text-gray-500">Top 20 debat-onderwerpen</p>
          {(debates.top_topics || []).length === 0 ? (
            <p className="text-[11px] text-gray-500 italic">Geen debatten in dit venster.</p>
          ) : (debates.top_topics || []).map((t, i) => (
            <div key={`${i}-${(t.topic || "").slice(0, 20)}`} className="border-b border-gray-100 py-1">
              <div className="flex justify-between gap-2">
                <p className="text-[11px] flex-1 truncate" title={t.topic}>{i + 1}. {t.topic}</p>
                <span className="text-[10px] font-mono font-bold flex-shrink-0">{t.count}×</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {data && tab === "support" && (
        <div className="p-3 space-y-3" data-testid="stats-support">
          <div className="border border-gray-200 p-2 flex items-center justify-between text-xs">
            <span>Support-berichten totaal</span>
            <span className="font-bold">{support.total_messages || 0}</span>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase text-gray-500 mb-1">Per intent</p>
            {(support.by_intent || []).length === 0 ? (
              <p className="text-[11px] text-gray-500 italic">Nog geen support-berichten in dit venster.</p>
            ) : (support.by_intent || []).map(s => (
              <div key={s.intent} className="flex justify-between text-[11px] py-0.5 border-b border-gray-100">
                <span>{s.intent}</span>
                <span className="font-mono font-bold">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
